/**
 * What the World agent decided about an agent that stopped (RFC 0348 3e), as
 * the state at the right of that stop's card.
 *
 * ONE RULE FOR EVERY THREAD. The World agent's thread is drawn in a pane and
 * in the World view (⌘.), and a stop card that said "waiting on you" in one and
 * nothing in the other would be two answers to one question. Both read this.
 */

import type { TranscriptMessage } from '@mod/core/model'
import {
  projectSessionActiveRun,
  projectSessionStopReports,
} from '@mod/core/model'
import { type ReactNode, useMemo } from 'react'
import { SubthreadChip } from './ThreadCard'

/** The verdict's chip for a stop event, or undefined to leave the card's own. */
export type StopTrailing = (message: TranscriptMessage) => ReactNode | undefined

const quiet = (text: string) => (
  <span className="text-[0.72rem] text-muted-foreground">{text}</span>
)

export function useStopTrailing(sessionDoc: unknown): StopTrailing {
  const reports = useMemo(
    () =>
      new Map(projectSessionStopReports(sessionDoc).map((r) => [r.entryId, r])),
    [sessionDoc],
  )
  const deciding = useMemo(
    () => projectSessionActiveRun(sessionDoc) !== null,
    [sessionDoc],
  )
  return useMemo(
    () => (message: TranscriptMessage) => {
      const report = message.entryId ? reports.get(message.entryId) : undefined
      if (!report) return undefined
      if (report.verdict === 'yours') return <SubthreadChip state="waiting" />
      if (report.verdict === 'answered') return quiet('answered')
      if (report.verdict === 'nothing') return <SubthreadChip state="done" />
      if (report.seen) return quiet('you had it open')
      if (deciding && report.status !== 'started') return quiet('deciding…')
      return undefined
    },
    [reports, deciding],
  )
}
